// Over welke propositie het Plan gaat (pilot 26-10, baan 2W). Het Plan was op
// ICT gebouwd (samen.ts PROPOSITIE = ICT, live13 sector ICT, teksten met
// "ICT-werk" en "Brainportregio"); nu opent het op de propositie van wie
// kijkt:
//
// - een AM of BP (rol accountmanagement of llo): zijn eigen propositie uit
//   /api/mij (`propositie` = de plansleutel, `startSector` = de datasector);
// - beheer: een kiezer met de pilotproposities die /api/meta als zichtbaar
//   noemt, zonder het meetinstrument ICT. De keuze blijft in deze browser.
//
// Eén actieve propositie per pagina (module-waarde, zoals sectorkeuze.ts):
// de teksten, de opslagsleutel, de trendvraag van 1.3, de trendlaag en de
// funding lezen hem op het moment dat ze iets nodig hebben. Pagina.tsx zet
// hem vóór de eerste render van het plan (inhoud-server.ts `activeer`).

import { useCallback, useEffect, useState } from "react";

import type { Mij } from "../../contracts";
import { api, ApiError } from "../../lib/api";
import { registerSectoren, STANDAARD_STARTSECTOR } from "../../lib/mij";
import { trendlaagAan } from "../trendlaag/schakelaar";
import { leesAdres } from "./lezen";
import { VOORBEELD_PROPOSITIE, VOORBEELD_WOORDEN } from "./voorbeeld-propositie";

export const PLAN_JAAR = 2026;

export type PlanPropositie = {
  /** De plansleutel: /api/plan/{propositie}/{jaar} en gebruikers.propositie. */
  propositie: string;
  /** De datasector van de run (trends, skills, trendlaag). */
  sector: string;
  /** Het woord op het scherm ("Economie & Ondernemen", "Kinderopvang (pilot)"). */
  naam: string;
  /** De regio in een zin ("in Zuidoost-Brabant"). */
  regio: string;
  jaar: number;
  /** De bedrijfstakken waarop de funding sleutelt (sectors.yaml). */
  fundingSectoren: string[];
  /** De meegebakken voorbeelddata (tests, proefpagina), niet de server. */
  voorbeeld: boolean;
  /** Wie kijkt (/api/mij id): de browseropslag is per persoon, zodat een AM en
   *  een BP die na elkaar in dezelfde browser inloggen elkaars keuzes niet
   *  overnemen (en als eigen keuze naar de server sturen). */
  gebruiker?: string;
};

/** De woorden die de teksten van het plan invullen. */
export type PlanWoorden = { naam: string; werk: string; hetWerk: string; regio: string; trends: string };

/** De regio van de pilot: alle vier de proposities werken vanuit Summa in Eindhoven. */
export const PILOT_REGIO = "Zuidoost-Brabant";

/**
 * De bedrijfstakken per pilotpropositie (config/proposities.yaml
 * pilotproposities[].sectoren). Alleen de terugval als beheer een andere
 * propositie kiest dan die waarop zijn schermen openen: voor de eigen
 * propositie rekent de service ze uit (/api/mij registerSectoren).
 */
const BEDRIJFSTAKKEN: Record<string, string[]> = {
  techniek: ["maakindustrie", "hightech"],
  economie_ondernemen: ["retail", "zakelijk"],
  veiligheid: ["zakelijk", "publiek"],
  zorg_welzijn: ["zorg", "publiek"],
};

// ── de actieve propositie ──────────────────────────────────────────────────

let actief: PlanPropositie = VOORBEELD_PROPOSITIE;

export function actievePlanPropositie(): PlanPropositie {
  return actief;
}

/** Zet de propositie waar het plan nu over gaat. Liever via inhoud-server.ts `activeer`. */
export function zetActievePlanPropositie(p: PlanPropositie): void {
  actief = p;
}

const kaal = (naam: string): string => naam.replace(/\s*\(pilot\)\s*$/i, "").trim();

export function planWoorden(p: PlanPropositie = actief): PlanWoorden {
  if (p.voorbeeld) return { ...VOORBEELD_WOORDEN };
  const n = kaal(p.naam);
  return { naam: n, werk: `het werk in ${n}`, hetWerk: `het werk in ${n}`, regio: p.regio, trends: `trends voor ${n}` };
}

/** De sleutel in de browser per propositie (en per persoon), zodat de stand van het ene plan
 *  niet in het andere lekt, en die van de AM niet in die van de BP op dezelfde computer. */
export function opslagSleutel(basis: string, p: PlanPropositie = actief): string {
  if (p.voorbeeld) return basis;
  return p.gebruiker ? `${basis}:${p.propositie}:${p.gebruiker}` : `${basis}:${p.propositie}`;
}

// ── welke propositie, voor wie ─────────────────────────────────────────────

/** `intern`: een sector die alleen beheer ziet (/api/meta, baan 2B); hoort nooit in een plan. */
export type PropositieOptie = { key: string; naam: string; sector: string; intern?: boolean };

export type PropositieKeuze =
  | { stand: "laden" }
  | { stand: "klaar"; p: PlanPropositie; opties: PropositieOptie[]; beheer: boolean }
  | { stand: "geen"; reden: string; opties: PropositieOptie[]; beheer: boolean };

/** De proposities waar een plan over kan gaan: de zichtbare, zonder het meetinstrument en zonder wat alleen beheer ziet. */
export function planOpties(meta: readonly PropositieOptie[] | null | undefined): PropositieOptie[] {
  return (meta ?? []).filter((o) => !o.intern && o.sector !== STANDAARD_STARTSECTOR && o.key !== STANDAARD_STARTSECTOR);
}

function maak(o: PropositieOptie, mij: Mij): PlanPropositie {
  const eigen = o.sector === mij.startSector && mij.registerSectoren?.length ? registerSectoren(mij) : null;
  return {
    propositie: o.key,
    sector: o.sector,
    naam: o.naam,
    regio: PILOT_REGIO,
    jaar: PLAN_JAAR,
    fundingSectoren: eigen ?? BEDRIJFSTAKKEN[o.key] ?? [],
    voorbeeld: false,
    ...(mij.id ? { gebruiker: mij.id } : {}),
  };
}

/**
 * De propositie van het plan, uit wie ik ben en wat zichtbaar is.
 * - AM of BP: mijn eigen propositie. Staat die niet tussen de zichtbare, dan
 *   toch die (de server beslist of het plan er is); naam uit /api/mij.
 * - Beheer: de gekozen propositie als die zichtbaar is, anders die van mijn
 *   startsector, anders de eerste zichtbare. Nooit het meetinstrument.
 */
export function bepaalPropositie(
  mij: Mij,
  meta: readonly PropositieOptie[] | null | undefined,
  gekozen: string | null,
  /** Alleen lezen (?lees=): dit plan, voor beheer en voor een meelezer; de server beslist of het mag. */
  lees: { propositie: string; jaar: number } | null = null,
): PropositieKeuze {
  const opties = planOpties(meta);
  const beheer = !!mij.isAdmin;
  if (lees) {
    const o = opties.find((x) => x.key === lees.propositie) ?? { key: lees.propositie, naam: lees.propositie, sector: lees.propositie };
    return { stand: "klaar", p: { ...maak(o, mij), jaar: lees.jaar }, opties, beheer };
  }
  if (!beheer) {
    const key = mij.propositie;
    if (!key || key === STANDAARD_STARTSECTOR) return { stand: "geen", reden: "Je account hoort nog niet bij een pilotpropositie.", opties, beheer };
    const o = opties.find((x) => x.key === key) ?? {
      key,
      naam: mij.startSectorLabel || mij.propositieLabel || key,
      sector: mij.startSector || key,
    };
    return { stand: "klaar", p: maak(o, mij), opties, beheer };
  }
  const o = opties.find((x) => x.key === gekozen) ?? opties.find((x) => x.sector === mij.startSector) ?? opties[0];
  if (!o) return { stand: "geen", reden: "Er staat nog geen pilotpropositie op het dashboard; zodra er een zichtbaar is, opent het plan hier.", opties, beheer };
  return { stand: "klaar", p: maak(o, mij), opties, beheer };
}

const KEUZE = "summa-plan-propositie";

function leesKeuze(): string | null {
  try {
    return localStorage.getItem(KEUZE);
  } catch {
    return null;
  }
}

/** De propositie van het plan voor wie nu kijkt; beheer kan wisselen (`kies`). */
export function usePlanPropositie(): { keuze: PropositieKeuze; kies: (key: string) => void; fout: string | null } {
  const [mij, setMij] = useState<Mij | null>(null);
  const [meta, setMeta] = useState<PropositieOptie[] | null>(null);
  const [gekozen, setGekozen] = useState<string | null>(leesKeuze);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    let weg = false;
    void (async () => {
      try {
        const ik = await api.mij();
        // Zonder /api/meta (een oudere dienst) alleen de eigen propositie.
        const m = await api.meta(ik.startSector ?? null).catch(() => null);
        if (weg) return;
        setMeta((m?.proposities ?? []) as PropositieOptie[]);
        setMij(ik);
      } catch (e) {
        if (weg) return;
        setFout(
          e instanceof ApiError
            ? e.status === 401
              ? "je bent niet ingelogd"
              : e.status === 0
                ? "de server is niet bereikbaar"
                : e.message || e.reason
            : "onbekende fout",
        );
      }
    })();
    return () => {
      weg = true;
    };
  }, []);

  const kies = useCallback((key: string) => {
    setGekozen(key);
    try {
      localStorage.setItem(KEUZE, key);
    } catch {
      /* geen opslag: alleen voor dit bezoek */
    }
  }, []);

  const keuze: PropositieKeuze = mij ? bepaalPropositie(mij, meta, gekozen, leesAdres()) : { stand: "laden" };
  return { keuze, kies, fout };
}

// ── de trendlaag van de propositie ─────────────────────────────────────────

let heeftTrendlaag = true;

/** Pagina.tsx zegt of deze propositie een trendlaag heeft (de stapel van 1.3 variant A is er en niet leeg). */
export function zetPlanTrendlaag(aan: boolean): void {
  heeftTrendlaag = aan;
}

/** 1.3 variant A (de trendlaag): alleen met de schakelaar aan én een trendlaag voor deze propositie. */
export const planTrendlaagAan = (): boolean => heeftTrendlaag && trendlaagAan();
